import type { ChangeEvent, ReactNode } from "react";

/**
 * Presentational form primitives, generalized from the realm-secret row that was
 * the app's only write. Deliberately admin-local: `@kontextwerk/web-common` is
 * runtime logic only (no React, no Tailwind). Nothing here imports anything
 * admin-specific, so promoting them later is a file move.
 */

const INPUT_CLASS =
  "w-full text-xs bg-kw-bg-elevated rounded p-2 text-kw-text-primary " +
  "border border-kw-border-subtle focus:outline-none focus:border-kw-l1 disabled:opacity-50";

export function SectionCard({
  title,
  description,
  children,
  footer,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <section className="border border-kw-border-subtle rounded bg-kw-bg-elevated/40">
      <header className="px-4 py-3 border-b border-kw-border-subtle">
        <h3 className="text-sm uppercase tracking-wide text-kw-text-muted">{title}</h3>
        {description && <p className="text-xs text-kw-text-faint mt-1">{description}</p>}
      </header>
      <div className="px-4 py-4 space-y-4">{children}</div>
      {footer && (
        <footer className="px-4 py-3 border-t border-kw-border-subtle">{footer}</footer>
      )}
    </section>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-kw-text-muted">{label}</span>
      {children}
      {hint && !error && <span className="block text-xs text-kw-text-faint">{hint}</span>}
      {error && <span className="block text-xs text-red-400">{error}</span>}
    </label>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
  mono,
  type = "text",
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
  type?: "text" | "password";
  disabled?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      className={`${INPUT_CLASS} ${mono ? "font-mono" : ""}`}
    />
  );
}

export function JsonTextArea({
  value,
  onChange,
  rows = 3,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <textarea
      value={value}
      rows={rows}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
      className={`${INPUT_CLASS} font-mono`}
    />
  );
}

export interface SelectOption {
  value: string;
  label: string;
  hint?: string;
  disabled?: boolean;
}

export function Select({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
}) {
  const active = options.find((o) => o.value === value);
  return (
    <>
      <select
        value={value}
        disabled={disabled}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
        className={INPUT_CLASS}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      {active?.hint && (
        <span className="block text-xs text-kw-text-faint mt-1">{active.hint}</span>
      )}
    </>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
  disabledReason,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
  /** Rendered when disabled — the operator needs the *why*, not a dead control. */
  disabledReason?: string;
}) {
  return (
    <div className="space-y-1">
      <label className="flex items-center gap-2 text-xs text-kw-text-primary">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
          className="accent-kw-l1 disabled:opacity-50"
        />
        <span className={disabled ? "opacity-50" : ""}>{label}</span>
      </label>
      {disabled && disabledReason && (
        <span className="block text-xs text-kw-text-faint pl-6">{disabledReason}</span>
      )}
    </div>
  );
}

/**
 * Reproduces the behaviour the one existing write got right: a validation
 * failure returns before `busy` is touched, success flashes for two seconds, and
 * a *failed* save leaves the form open with the input intact so the operator can
 * fix and retry. A 403 renders amber rather than red — "your token cannot" is a
 * different fact from "your input is wrong".
 */
export function SaveBar({
  dirty,
  busy,
  error,
  errorKind,
  savedAt,
  onSave,
  onReset,
  saveLabel = "Save",
  disabled,
  disabledReason,
}: {
  dirty: boolean;
  busy: boolean;
  error?: string;
  errorKind?: string;
  savedAt?: number | null;
  onSave: () => void;
  onReset: () => void;
  saveLabel?: string;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const blocked = disabled || busy || !dirty;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 text-xs">
        <button
          onClick={onSave}
          disabled={blocked}
          className="px-3 py-1.5 rounded bg-kw-l1 text-black disabled:opacity-50"
        >
          {busy ? "Saving…" : saveLabel}
        </button>
        <button
          onClick={onReset}
          disabled={busy || !dirty}
          className="text-kw-text-muted hover:text-kw-text-primary disabled:opacity-50"
        >
          Reset
        </button>
        {savedAt && <span className="text-kw-l2">✓ saved</span>}
      </div>
      {disabled && disabledReason && (
        <div className="text-xs text-kw-text-faint">{disabledReason}</div>
      )}
      {error && (
        <div className={`text-xs ${errorKind === "forbidden" ? "text-amber-400" : "text-red-400"}`}>
          {error}
        </div>
      )}
    </div>
  );
}
